import {
  Layout,
  Segmented,
  notification,
  Spin,
  Button,
  Popconfirm,
  Form,
  Tooltip,
  Input,
  Card,
  Typography,
} from "antd";
import React, { useState, useContext, useEffect } from "react";
import LayoutBaseAdmin from "components/LayoutBaseAdmin";
import LayoutBaseBusiness from "components/SuperAdminAndBusiness/LayoutBaseBusiness";
import { AuthContext } from "contexts/AuthContext";
import { useTranslation } from "react-i18next";
import ComplaintHeader from "components/ComplaintDetails/ComplaintHeader";
import ComplaintDetailsSection from "components/ComplaintDetails/ComplaintDetailsSection";
import OriginalReportSection from "components/ComplaintDetails/OriginalReportSection";
import queryString from "query-string";
import {
  getReportByCodeAdmin,
  updateClosureRequestReport,
  updateReport,
  updateReportWasRead,
} from "services/reportService";

import EvidenceSection from "components/ComplaintDetails/EvidenceSection";
import AttachmentsSection from "components/ComplaintDetails/AttachmentsSection";
import { PrimaryButton } from "components/SuperAdminAndBusiness/PrimaryButton";
import { useNavigate } from "react-router-dom";
import { postInboxMessages } from "services/inboxMessages.service";
import { postEmailTest } from "services/sendEmails.service";
import ReactQuill from "react-quill";

const { Content } = Layout;
const { Text, Title } = Typography;

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" },
    ],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const actualDomain = window.location.host;

const ComplaintDetails: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { code } = queryString.parse(window.location.search);
  const navigate = useNavigate();
  const LayoutComponent =
    user &&
    (user.type === "supervisor" ||
      user.type === "admin" ||
      user.type === "superAdmin")
      ? LayoutBaseBusiness
      : LayoutBaseAdmin;

  const [loading, setLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<any>([]);

  const [activeTab, setActiveTab] = useState("details");
  const [activeSubTab, setActiveSubTab] = useState("details");

  const [form] = Form.useForm();
  const [bodyMessage, setBodyMessage] = useState<string>("");

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === "details") {
      setActiveSubTab("details");
    }
  };

  const handleSubTabChange = (value: string) => {
    setActiveSubTab(value);
  };

  const getReportData = async () => {
    try {
      setLoading(true);

      if (code && typeof code === "string") {
        const response = await getReportByCodeAdmin(code);

        setBodyMessage(response.emailResponse);
        form.setFieldsValue({
          subject: response.subjectResponse,
          text: response.emailResponse,
        });

        setReportData(response);

        if (response && response.uuid && response.wasRead === false) {
          await updateReportWasRead(response.uuid);
        }
      }
    } catch (error: any) {
      notification.error({
        placement: "bottomRight",
        message:
          error?.response?.data?.message ||
          t("anErrorOccurredDuringTheOperation"),
      });
    } finally {
      setLoading(false);
    }
  };

  // const handleDeleteComplaint = async () => {
  //   try {
  //     await deleteReport(reportData.uuid);
  //     notification.success({
  //       placement: "bottomRight",
  //       message: t("complaintDeletedSuccessfully"),
  //     });
  //     navigate(
  //       `/${
  //         user &&
  //         (user.type === "supervisor" ||
  //           user.type === "admin" ||
  //           user.type === "superAdmin")
  //           ? "business"
  //           : "analyst"
  //       }/intake`
  //     );
  //   } catch (error: any) {
  //     notification.error({
  //       placement: "bottomRight",
  //       message:
  //         error?.response?.data?.message ||
  //         t("anErrorOccurredDuringTheOperation"),
  //     });
  //   }
  // };

  const handleCloseComplaint = async () => {
    try {
      const response = await updateClosureRequestReport(reportData.uuid, {
        approveRequest: true,
      });

      if (response) {
        notification.success({
          placement: "bottomRight",
          message: t("complaintClosed"),
        });
      }
      navigate(
        `/${
          user &&
          (user.type === "supervisor" ||
            user.type === "admin" ||
            user.type === "superAdmin")
            ? "business"
            : "analyst"
        }/intake`
      );
    } catch (error: any) {
      notification.error({
        placement: "bottomRight",
        message:
          error?.response?.data?.message ||
          t("anErrorOccurredDuringTheOperation"),
      });
    }
  };

  const handleSendEmailAndInboxMessage = async () => {
    try {
      setLoading(true);

      const messageInbox = bodyMessage
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/?p[^>]*>/gi, "");

      const messageWithLinkToRedirect = `
      ${bodyMessage}

      <p style="margin: 20px 0;">
        <a href="https://${actualDomain}/whistleblower/inbox?code=${
        reportData.code
      }" target="_blank" rel="noopener noreferrer" style="display: inline-block; padding: 10px 20px; background-color: #33336d; color: white; text-decoration: none; border-radius: 5px;">
          ${t("clickHereToGoToTheReport")}
        </a>
      </p>
    `;

      if (user && user.uuid) {
        if (reportData && reportData.email) {
          await postEmailTest({
            email: reportData.email,
            subject: form.getFieldValue("subject"),
            messageTest: messageWithLinkToRedirect,
          });
        }

        await postInboxMessages({
          message: messageInbox,
          files: [],
          viewed: false,
          reportCode: reportData.code,
          sender: user.uuid,
        });

        await updateReport(reportData.uuid, {
          emailResponseSent: true,
          subjectResponse: form.getFieldValue("subject"),
          emailResponse: bodyMessage,
        });

        notification.success({
          placement: "bottomRight",
          message: t("messagesSentSuccessfully"),
        });

        getReportData();
      }
    } catch (error: any) {
      notification.error({
        placement: "bottomRight",
        message:
          error?.response?.data?.message ||
          t("anErrorHasOccurredWhileSendingMessages"),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (code && typeof code === "string") {
      getReportData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const renderContent = () => {
    if (activeTab === "details") {
      return (
        <div className="space-y-4 md:space-y-6">
          <div className="hidden md:block">
            <Segmented
              value={activeSubTab}
              onChange={handleSubTabChange}
              options={[
                { label: t("details"), value: "details" },
                { label: t("originalReport"), value: "original" },
              ]}
              className="w-full md:w-auto mb-4"
            />
          </div>
          <div className="block md:hidden">
            <Segmented
              value={activeSubTab}
              onChange={handleSubTabChange}
              options={[
                { label: t("details"), value: "details" },
                { label: t("originalReport"), value: "original" },
              ]}
              className="w-full md:w-auto mb-4"
              block
            />
          </div>
          {activeSubTab === "details" ? (
            <ComplaintDetailsSection
              reportData={reportData}
              refetchReportData={getReportData}
            />
          ) : (
            <OriginalReportSection reportData={reportData} />
          )}
        </div>
      );
    } else if (activeTab === "acknowledgeReceipt") {
      return (
        <div className="flex flex-col gap-8">
          <Card className="bg-white-A700 shadow-md">
            <Title level={4} className="!mb-0 !text-lg md:!text-xl">
              {t("instructions")}
            </Title>
            <div className="flex flex-col gap-5">
              <Text type="secondary" className="text-sm md:text-base">
                {t("aiGeneratedDraftEmailInstructions")}
              </Text>

              <div className="flex flex-col gap-1">
                <Text type="secondary" className="text-sm md:text-base">
                  {t("aiGeneratedDraftEmailInstructionsFirst")}
                </Text>

                <Text type="secondary" className="text-sm md:text-base">
                  {t("aiGeneratedDraftEmailInstructionsSecond")}
                </Text>

                <Text type="secondary" className="text-sm md:text-base">
                  {t("aiGeneratedDraftEmailInstructionsThird")}
                </Text>

                <Text type="secondary" className="text-sm md:text-base">
                  {t("aiGeneratedDraftEmailInstructionsFourth")}
                </Text>
              </div>
            </div>
          </Card>

          <Card className="bg-white-A700 shadow-md">
            <Title level={4} className="!mb-0 !text-lg md:!text-xl">
              {t("draftCommunication")}
            </Title>

            {reportData.emailResponseSent && (
              <div className="mt-2 mb-5">
                <Text type="secondary" className="text-sm md:text-base">
                  {t("emailOrInboxAlreadySent")}
                </Text>
              </div>
            )}

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSendEmailAndInboxMessage}
              className="w-full"
            >
              <Form.Item
                label={t("subject")}
                name="subject"
                className="w-[100%]"
                rules={[
                  {
                    required: true,
                    message: t("enterAValidSubject"),
                  },
                ]}
              >
                <Input
                  className="rounded-md"
                  placeholder={t("subject")}
                  id="subject"
                  size="large"
                  disabled={reportData.emailResponseSent}
                  readOnly={reportData.emailResponseSent}
                />
              </Form.Item>
              <Form.Item
                label={t("message")}
                name="text"
                rules={[
                  {
                    required: true,
                    message: t("enterAValidMessage"),
                  },
                ]}
              >
                <ReactQuill
                  id="text"
                  formats={formats}
                  modules={modules}
                  value={bodyMessage}
                  className={` ${
                    reportData.emailResponseSent
                      ? "bg-[#f6f6f6] text-[#c0c0c0] cursor-not-allowed"
                      : ""
                  }`}
                  onChange={(value) => {
                    setBodyMessage(value);
                    form.setFieldsValue({ text: value });
                  }}
                  readOnly={reportData.emailResponseSent}
                />
              </Form.Item>
              <Tooltip
                title={
                  reportData.emailResponseSent
                    ? t("messageIsAlreadySent")
                    : t("sendMessage")
                }
              >
                <Button
                  htmlType="submit"
                  disabled={reportData.emailResponseSent}
                  className="text-blue_gray-900"
                >
                  {loading ? t("sending") : t("sendMessage")}
                </Button>
              </Tooltip>
            </Form>
          </Card>
        </div>
      );
    }
    return (
      <div className="space-y-4 md:space-y-6">
        <AttachmentsSection reportData={reportData} />
        <EvidenceSection reportData={reportData} />
      </div>
    );
  };

  return (
    <LayoutComponent>
      <Spin spinning={loading} fullscreen />
      <ComplaintHeader
        complaintNumber={reportData.code}
        client={reportData.reportIssues?.[0]?.complaint || ""}
        status={
          reportData.status === "Analysis finished" ? "active" : "inactive"
        }
        backRoute={`/${
          user &&
          (user.type === "supervisor" ||
            user.type === "admin" ||
            user.type === "superAdmin")
            ? "business"
            : "analyst"
        }/intake`}
      />

      <Content className="py-3 md:py-6">
        <div className="hidden md:block">
          <Segmented
            value={activeTab}
            onChange={handleTabChange}
            options={[
              { label: t("complaintDetails"), value: "details" },
              { label: t("evidence"), value: "evidence" },
              { label: t("acknowledgeReceipt"), value: "acknowledgeReceipt" },
            ]}
            className="w-full md:w-auto mb-4 md:mb-6"
          />
        </div>
        <div className="block md:hidden">
          <Segmented
            value={activeTab}
            onChange={handleTabChange}
            options={[
              { label: t("complaintDetails"), value: "details" },
              { label: t("evidence"), value: "evidence" },
              { label: t("acknowledgeReceipt"), value: "acknowledgeReceipt" },
            ]}
            className="w-full md:w-auto mb-4 md:mb-6"
            block
            vertical
          />
        </div>

        <div className="flex flex-col md:flex-row md:justify-end mb-5 gap-2">
          {/* <Popconfirm
            title={t(
              "areYouSureYouWantToDeleteThisComplaintThisActionCannotBeUndone"
            )}
            onConfirm={handleDeleteComplaint}
            okText={t("delete")}
            cancelText={t("cancel")}
          >
            <Button type="default" size="large">
              {t("deleteComplaint")}
            </Button>
          </Popconfirm> */}

          {reportData?.reportStatus?.status !== "CLOSED" && (
            <Popconfirm
              title={t(
                "areYouSureYouWantToCloseThisComplaintThisActionCannotBeUndone"
              )}
              onConfirm={handleCloseComplaint}
              okText={t("close")}
              cancelText={t("cancel")}
            >
              <PrimaryButton size="large">{t("closeComplaint")}</PrimaryButton>
            </Popconfirm>
          )}
        </div>

        {!loading && renderContent()}
      </Content>
    </LayoutComponent>
  );
};

export default ComplaintDetails;
